/**
 * FocusTrap — piège le focus clavier à l'intérieur d'un conteneur.
 *
 * Usage :
 *
 *   import FocusTrap from '../utils/FocusTrap';
 *
 *   const trap = new FocusTrap(document.querySelector('.ma-modale'));
 *
 *   // À l'ouverture — passe le bouton déclencheur pour y revenir à la fermeture
 *   trap.activate(boutonOuverture);
 *
 *   // À la fermeture — libère le piège et rend le focus au déclencheur
 *   trap.deactivate();
 *
 * Éléments focusables pris en compte : liens, boutons, inputs, selects,
 * textareas, et tout élément avec un tabindex >= 0.
 * Les éléments dans un conteneur aria-hidden="true" ou hidden sont exclus.
 */

const FOCUSABLE_SELECTORS = [
	'a[href]',
	'button:not([disabled])',
	'input:not([disabled])',
	'select:not([disabled])',
	'textarea:not([disabled])',
	'[tabindex]:not([tabindex="-1"])',
].join(', ');

export default class FocusTrap {
	constructor(container) {
		this.container = container;
		this._onKeyDown = this._onKeyDown.bind(this);
	}

	activate(triggerEl = null) {
		this.triggerEl = triggerEl;
		const focusable = this._getFocusable();
		if (focusable.length) focusable[0].focus();
		document.addEventListener('keydown', this._onKeyDown);
	}

	deactivate() {
		document.removeEventListener('keydown', this._onKeyDown);
		this.triggerEl?.focus();
		this.triggerEl = null;
	}

	_getFocusable() {
		return [...this.container.querySelectorAll(FOCUSABLE_SELECTORS)]
			.filter(el => !el.closest('[aria-hidden="true"]') && !el.closest('[hidden]'));
	}

	_onKeyDown(e) {
		if (e.key !== 'Tab') return;
		const focusable = this._getFocusable();
		if (!focusable.length) return;
		const first = focusable[0];
		const last = focusable[focusable.length - 1];
		if (e.shiftKey) {
			if (document.activeElement === first) {
				e.preventDefault();
				last.focus();
			}
		} else {
			if (document.activeElement === last) {
				e.preventDefault();
				first.focus();
			}
		}
	}
}